import { useEffect, useMemo, type ReactElement } from 'react';
import Select, { type OptionType } from '@atlaskit/select';
import EmptyState from '@atlaskit/empty-state';
import { FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { useFeatureGate } from '@atlassian/feature-gates-react';
import {
  useSelectedWorkspace,
  useInfo,
} from '../../../../Flow/ConsentContextProvider';
import { OAuthConsentFeatureGate } from '../../../../FeatureGates/FeatureGates';
import ErrorImage from '#assets/error.svg';

interface SiteSelectorMessages {
  siteLabel: MessageDescriptor;
  sitePlaceholder: MessageDescriptor;
  noWorkspacesHeading: MessageDescriptor;
  noWorkspacesDescription: MessageDescriptor;
  noWorkspacesLearnMore: MessageDescriptor;
}

export interface SiteSelectorProps {
  messages: SiteSelectorMessages;
  /** URL for the "Learn more" link shown when no workspaces are available. */
  noWorkspacesLearnMoreUrl: string;
  inputId: string;
}

const siteSelectStackStyles = xcss({
  alignSelf: 'stretch',
});

const siteSelectStyles = xcss({
  width: '100%',
  maxWidth: '350px',
});

const SiteSelector: React.FC<SiteSelectorProps> = ({
  messages,
  noWorkspacesLearnMoreUrl,
  inputId,
}): ReactElement => {
  const { formatMessage } = useIntl();
  const info = useInfo();
  const { selectedWorkspace, setSelectedWorkspace } = useSelectedWorkspace();
  const shouldSortWorkspaces = useFeatureGate(
    OAuthConsentFeatureGate.MCP_SORT_WORKSPACES,
  );

  const workspaces = useMemo(() => {
    const availableWorkspaces = info.availableWorkspaces || [];

    if (!shouldSortWorkspaces) {
      return availableWorkspaces;
    }

    return [...availableWorkspaces].sort();
  }, [info.availableWorkspaces, shouldSortWorkspaces]);

  // Auto-select when there is exactly one site available, so the user doesn't
  // have to open a single-option dropdown. Mirrors the Trello workspace
  // prefill. Only fires when nothing is selected yet.
  useEffect(() => {
    if (workspaces.length === 1 && !selectedWorkspace) {
      setSelectedWorkspace?.(workspaces[0]);
    }
  }, [workspaces, selectedWorkspace, setSelectedWorkspace]);

  if (workspaces.length === 0) {
    const learnMoreLink = (
      <Link
        href={noWorkspacesLearnMoreUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        <FormattedMessage {...messages.noWorkspacesLearnMore} />
      </Link>
    );

    return (
      <EmptyState
        header={formatMessage(messages.noWorkspacesHeading)}
        description={
          <FormattedMessage
            {...messages.noWorkspacesDescription}
            values={{ learnMoreLink }}
          />
        }
        imageUrl={ErrorImage}
      />
    );
  }

  // Convert workspaces array to options format
  const workspaceOptions: OptionType[] = workspaces.map((workspace) => ({
    label: workspace,
    value: workspace,
  }));

  // Find the selected option object from the value
  const selectedOption = workspaceOptions.find(
    (option) => option.value === selectedWorkspace,
  );

  return (
    <Stack space="space.100" xcss={siteSelectStackStyles}>
      <Box as="label" htmlFor={inputId}>
        <Heading size="small">{formatMessage(messages.siteLabel)}</Heading>
      </Box>
      <Box xcss={siteSelectStyles}>
        <Select
          inputId={inputId}
          options={workspaceOptions}
          placeholder={formatMessage(messages.sitePlaceholder)}
          value={selectedOption}
          onChange={(option) => {
            if (setSelectedWorkspace) {
              setSelectedWorkspace(option?.value ? String(option.value) : null);
            }
          }}
        />
      </Box>
    </Stack>
  );
};

export default SiteSelector;
